import { EventEmitter } from 'events';
import Immutable from 'immutable';
import { CargoType, AttributeType } from 'types';
import Dispatcher from 'core/Dispatcher';
import Actions from 'constants/Actions';
import CategoryStore from 'stores/CategoryStore';
import EditingCargoStore from 'stores/EditingCargoStore';
import CargosConstants from 'constants/Cargos';

const categoriesConfig = require('../../configs/categories.config.json');
/**
 * @constant
 * @type {Object.<number, CargoType>}
 */
const cargosConfig = require('../../configs/cargos.config.json');
const attributesConfig = require('../../configs/attributes.config.json');

const CHANGE_EVENT = 'change';

/**
 * Коллекция возможных атрибутов в системе.
 * @type {Object.<number, AttributeType>}
 */
const _allAttributes = Object.values(attributesConfig).reduce((result, attribute) => {
    var values = {};
    if (attribute.values) {
        values = attribute.values.reduce((result, value) => {
            result[value.id] = value;
            return result;
        }, {});
    }

    result[attribute.id] = new AttributeType({
        ...attribute,
        values
    });

    return result;
}, {});

/**
 * Коллекция возможных типов грузов в выбранной категории.
 * @type {CargoType[]}
 */
var _allCargos = [];

/**
 * Список грузов, добавленных в заказ.
 * @type {Immutable.List.<CargoType>}
 */
var _deliveryCargos = Immutable.List();

/**
 * Позиция редактируемого груза в списке заказа.
 * Если это новый груз и его еще нет в списке, позиция равна -1.
 * @type {number}
 */
var _editingCargoIndex = -1;

/**
 * Выбран ли режим выбора типа нового груза.
 * Этот режим включает отображение подкатегорий под списком грузов.
 * @type {boolean}
 */
var _isCargoTypesShown = true;

/**
 * Общие параметры заказа (общий вес и объём)
 * @type {Immutable.Map}
 */
var _totalMeasurements = Immutable.Map({
    'total_weight': 0, // always in kg
    'total_volume': 0, // always in m³
    'is_changeable': false,
    'is_auto_total_measurements_count_on': true,
});

function findCargoInDelivery(cargoId) {
    return _deliveryCargos.findIndex(function (cargoType) {
        return cargoType.id == cargoId;
    });
}

/**
 * Добавляет новый груз в список доставки
 * @param {CargoType} cargoType
 */
function addDeliveryCargo(cargoType) {
    _deliveryCargos = _deliveryCargos.push(cargoType);
}

function removeDeliveryCargo(cargoId) {
    var index = findCargoInDelivery(cargoId);
    if (index < 0) return;
    _deliveryCargos = _deliveryCargos.delete(index);
}

function updateDeliveryCargo(cargoId, key, value) {
    var index = findCargoInDelivery(cargoId);
    if (index < 0) return;
    var cargoType = _deliveryCargos.get(index);
    if (!cargoType.attributes.has(key)) return;

    cargoType = cargoType.withMutations(cargoType => {
        if (key === 'name') {
            cargoType.set('name', value);
        }

        var attribute = cargoType.attributes.get(key).set('value', value);
        cargoType.setIn(['attributes', key], attribute);
    });


    _deliveryCargos = _deliveryCargos.set(index, cargoType);
}

/**
 * Удаляет груз из заказа.
 * @param {number} cargoId Номер груза в списке
 */
function removeCargo(cargoId) {
    _deliveryCargos = _deliveryCargos.delete(cargoId);
}

/**
 * @param {number[]} attributesIds
 * @return {Object}
 */
function makeAttributesObject(attributesIds) {
    return attributesIds.reduce((result, id) => {
        var attribute = _allAttributes[id];
        result[attribute.name] = attribute;
        return result;
    }, {});
}

/**
 * @param {Object} cargo
 * @param {Object} attributes
 * @return {CargoType}
 */
function createCargoType(cargo, attributes, isReplaceId = false) {

    if (isReplaceId && cargo.id) {
        // Т.к. cargo - это не конкретный груз, а подкатегория
        const stuff_type_id = cargo.id;
        cargo.id = undefined;
        cargo.stuff_type_id = stuff_type_id;
    }

    return new CargoType({
        ...cargo,
        attributes: new Immutable.Map(attributes)
    });
}

function initializeCargos() {
    const selectedCategoryId = CategoryStore.getSelected().id;
    const category = categoriesConfig[selectedCategoryId];

    _allCargos = category.cargos.map(cargoId => {
        const attributes = makeAttributesObject(category.attributes);
        return createCargoType(cargosConfig[cargoId], attributes, true);
    });
}

/**
 * Сохранаяет изменеия аттрибутов груза и сохраняет его в списке.
 */
function stopCargoEditing() {
    const editingCargo = EditingCargoStore.getEditingCargo();

    if (_editingCargoIndex === -1) {
        _deliveryCargos = _deliveryCargos.push(editingCargo);
    } else {
        _deliveryCargos = _deliveryCargos.set(_editingCargoIndex, editingCargo);
    }
}

function generateCargo(cargo, newAttributes = {}, clearDelivery = false) {
    if (clearDelivery) {
        _deliveryCargos = _deliveryCargos.clear();
    }

    if (Object.keys(newAttributes).length > 0) {

        var attributes = cargo.attributes.withMutations((attributes) => {

            for (let key in newAttributes) {
                let value = newAttributes[key];

                attributes.setIn([key, 'value'], value);
            }
        });

        cargo = cargo.set('attributes', attributes);
    }

    addDeliveryCargo(cargo);

    return cargo;
}

function updateTotalMeasurements(totalVolume = 0, totalWeight = 0) {
    if (_totalMeasurements.get('is_auto_total_measurements_count_on') || CategoryStore.getSelected().id === 'moving') {
        _deliveryCargos.forEach((item) => {
            let addUnits = null;

            if (item.getIn(['attributes', 'unit_of_measurement', 'value'])) {
                addUnits = parseInt(item.getIn(['attributes', 'unit_of_measurement', 'value'])[0]);
            }

            let addQuantity = parseFloat(item.getIn(['attributes', 'quantity', 'value']));
            let addVolume = parseFloat(item.getIn(['attributes', 'volume', 'value']));
            let addWeight = parseFloat(item.getIn(['attributes', 'weight', 'value']));

            if (!addQuantity) {
                addQuantity = 1;
            }

            if (addUnits && addVolume) {
                if (addUnits === CargosConstants.UNITS['CM']) {
                    addVolume /= 1.0e+6; // cm3 to m3
                }

                totalVolume += addVolume * addQuantity;
            }

            if (addUnits && addWeight) {
                if (addUnits === CargosConstants.UNITS['M']) {
                    addWeight *= 1.0e+3; // tonnes to kg
                }

                totalWeight += addWeight * addQuantity;
            }
        });

        _totalMeasurements = _totalMeasurements.set('total_volume', totalVolume);
        _totalMeasurements = _totalMeasurements.set('total_weight', totalWeight);
    } else {
        return false;
    }
}

function setTotalMeasurements(totalWeight, totalVolume, isAutoTotalMeasurementsCountOn) {
    totalWeight = totalWeight ? parseFloat(totalWeight) : 0;
    totalVolume = totalVolume ? parseFloat(totalVolume) : 0;

    if (isAutoTotalMeasurementsCountOn === undefined) {
        isAutoTotalMeasurementsCountOn = false;
    }

    _totalMeasurements = _totalMeasurements.withMutations(result => {
        result.set('is_auto_total_measurements_count_on', isAutoTotalMeasurementsCountOn)
            .set('is_changeable', false)
            .set('total_weight', totalWeight)
            .set('total_volume', totalVolume);
    });
}

function changeTotalMeasurements(isChangeable) {
    _totalMeasurements = _totalMeasurements.set('is_changeable', isChangeable);
}

class CargosStore extends EventEmitter {

    /**
     * Возвращает список всех грузов.
     * @return {Object.<number, CargoType>}
     */
    getAllCargos() {
        return _allCargos;
    }

    /**
     * Возвращает груз по ID.
     * @param {number} id
     * @return {CargoType}
     */
    getCargoById(id) {
        return _allCargos.find((cargo) => cargo.id === id);
    }

    getCargoByStuffTypeId(id) {
        return _allCargos.find((cargo) => cargo.stuff_type_id === id);
    }

    isSubcategory(cargo) {

        if (!cargo) {
            return false;
        }

        return cargo.hasOwnProperty('predefined');
    }

    /**
     * Возвращает "конечный" груз по идентификатору (из конфигурации грузов)
     * @param {number} cargoId
     * @param {number} stuffTypeId
     * @return {Object}
     */
    getCargo(cargoId, stuffTypeId) {
        if (!stuffTypeId) return null;
        if (!cargosConfig.hasOwnProperty(stuffTypeId.toString())) return null;
        
        var subcategory = cargosConfig[stuffTypeId];
        if (!this.isSubcategory(subcategory)) return null;
        if (!subcategory.predefined) return null;
        
        for (var i = 0; i < subcategory.predefined.length; i++) {
            if (subcategory.predefined[i].id == cargoId) break;
        }
        
        if (i >= subcategory.predefined.length) return null;
    
        if (subcategory.predefined[i].attributes) {
            // Not consistent data from server
            subcategory.predefined[i].attributes = subcategory.predefined[i].attributes.map(attribute => {
                if (attribute.name === 'unit_of_measurement' && attribute.value === undefined) {
                    return {
                        ...attribute,
                        value: [CargosConstants.UNITS.CM],
                    }
                }
        
                return attribute
            })
        }
        
        return subcategory.predefined[i];
    }

    getSubcategory(stuffTypeId) {
        if (!cargosConfig.hasOwnProperty(stuffTypeId.toString())) return null;
        var subcategory = cargosConfig[stuffTypeId];
        if (!this.isSubcategory(subcategory)) return null;
        return subcategory;
    }

    /**
     * Возвращает список товаров подкатегории.
     * @param {number} cargoId
     * @return {CargoType[]}
     */
    getSubcategoryCargos(cargoId) {
        var list = [];
        var cargo = cargosConfig[cargoId];

        if (!cargo) return list;
        if (!this.isSubcategory(cargo)) return list;
        if (!cargo.predefined) return list;
        for (var i = 0; i < cargo.predefined.length; i++) {
            list.push(cargo.predefined[i]);
        }
        return list;
    }

    /**
     * @param parentCargoId
     * @return {CargoType[]}
     */
    getSubcategories(parentCargoId) {
        var list = [];

        for (var id in cargosConfig) {
            if (!cargosConfig.hasOwnProperty(id)) continue;
            var subcategory = cargosConfig[id];
            if (subcategory.parent_id == parentCargoId) list.push(subcategory);
        }
        return list;
    }

    getTotalMeasurements() {
        return _totalMeasurements;
    }

    creteCargoTypeByCategory(categoryId) {
        if (!categoriesConfig.hasOwnProperty(categoryId.toString())) return null;
        var category = categoriesConfig[categoryId];
        const attributes = makeAttributesObject(category.attributes);

        return new CargoType({
            id: null,
            name: "",
            "stuff_type_id": null,
            attributes: new Immutable.Map(attributes)
        });
    }

    createCargoType(cargo) {
        const selectedCategoryId = CategoryStore.getSelected().id;
        const category = categoriesConfig[selectedCategoryId];

        const attributes = makeAttributesObject(category.attributes);
        var cargoType = createCargoType(cargo, attributes);

        // Предустановка значений атрибутов из груза
        for (var i = 0; i < cargo.attributes.length; i++) {
            var srcAttribute = cargo.attributes[i];
            if (!cargoType.attributes.has(srcAttribute.name)) continue;
            var attribute = cargoType.attributes.get(srcAttribute.name).set('value', srcAttribute.value);
            cargoType = cargoType.setIn(['attributes', srcAttribute.name], attribute);
        }
        return cargoType;
    }

    getDeliveryCargo(cargoId) {
        var index = findCargoInDelivery(cargoId);
        if (index < 0) return null;
        return _deliveryCargos.get(index);
    }

    getDeliveryCargoByIndex(index) {
        if (index < 0) return null;
        return _deliveryCargos.get(index);
    }

    clearDeliveryCargos() {
        _deliveryCargos = _deliveryCargos.clear();
    }

    getCargoIndex() {
        return _editingCargoIndex;
    }

    /**
     * Возвращает груз по ID.
     * @param {number} id
     * @return {CargoType}
     */
    getCargoAttributeById(id) {
        return _allAttributes[id];
    }

    /**
     * Возвращает список всех грузов.
     * @return {Immutable.List.<CargoType>}
     */
    getDeliveryCargos() {
        return _deliveryCargos;
    }

    /**
     * @return {boolean}
     */
    isCargoTypesShown() {
        return _isCargoTypesShown;
    }

    /**
     * @param {CargoType} cargo
     * @return {Object}
     */
    normalizeCargo(cargo) {
        if (!cargo) {
            return {}
        }

        const category = CategoryStore.getSelected();
        const unitOfMeasurement = cargo.attributes.get("unit_of_measurement");
        const isWhl = cargo.attributes.has('length') && cargo.attributes.has('width') && cargo.attributes.has('height');
        var isWhlFully = false;

        if (isWhl) isWhlFully = cargo.attributes.get('length').get('value') && cargo.attributes.get('length').get('value') != "" &&
            cargo.attributes.get('width').get('value') && cargo.attributes.get('width').get('value') != "" &&
            cargo.attributes.get('height').get('value') && cargo.attributes.get('height').get('value') != "";

        const cargoId = parseInt(cargo.id);

        var resultCargo = {
            id: cargoId > 0 ? cargoId : null,
            stuff_type_id: cargo.stuff_type_id,
            name: (cargo.attributes.has('name') ? cargo.attributes.get('name').value : ""),
            attributes: cargo.attributes.reduce((result, attribute) => {
                let { id, value } = attribute;

                // Length, width, height
                if (id === 2 || id === 3 || id === 4) {

                    value = parseFloat(value);

                    if (isNaN(value)) {
                        return result;
                    }
                }

                /** @todo Костыль: Сервер интерпретирует значение объёма (в отличие от длины, ширины, высоты и веса) только в м3, вне зависи от указанных единиц измерения */
                if (id == 5) {

                    value = parseFloat(value);
                    if (isNaN(value)) {
                        return result;
                    }

                    if (unitOfMeasurement &&
                        unitOfMeasurement.get('value') &&
                        unitOfMeasurement.get('value')[0] === CargosConstants.UNITS.CM) {
                        value /= 1000000;
                    }
                }

                if (id == 5 && isWhl && !isWhlFully && category.get('name') !== 'book_separate_car') {
                    // return result;
                }

                // Weight
                if (id === 6) {

                    value = parseFloat(value);
                    if (isNaN(value)) {
                        return result;
                    }
                }

                if (id === 0 || value === undefined) {
                    return result;
                }

                if (typeof value === 'boolean') {
                    value = value ? 1 : 0;
                }

                result[id] = value;
                return result;
            }, {})
        }

        if (cargo.system_id && cargo.system_id > 0) {
            resultCargo.system_id = cargo.system_id;
        }

        return resultCargo;
    }

    denormalizeCargo(cargo) {
        if (!cargo) {
            return {}
        }

        const selectedCategoryId = CategoryStore.getSelected().id;
        const category = categoriesConfig[selectedCategoryId];
        const categoryAttributes = makeAttributesObject(category.attributes);
        const unitOfMeasurement = cargo.attributes && cargo.attributes[7] ? cargo.attributes[7] : null;

        var attributes = {};
        for (let id in cargo.attributes) {

            let attribute = _allAttributes[id];
            let value = cargo.attributes[id];

            if (attribute) {

                if (attribute.get('type') === 'checkbox') {
                    value = value ? true : false;
                }

                // From server we always have volume in m3
                if (attribute.get('name') === 'volume' &&
                    unitOfMeasurement &&
                    unitOfMeasurement[0] === CargosConstants.UNITS.CM) {
                    value *= 1000000;
                }

                attributes[attribute.get('name')] = attribute.set('value', value);
            }
        }

        var cargoType = createCargoType(cargo, Object.assign(categoryAttributes, attributes));

        return generateCargo(cargoType, {});
    }

    getNormalizedState() {
        var result = [];
        _deliveryCargos.forEach(cargo => {
            result.push(this.normalizeCargo(cargo));
        });

        return result;
    }

    getTotalWeight() {
        return _totalMeasurements.get('total_weight');
    }

    getTotalVolume() {
        return _totalMeasurements.get('total_volume');
    }

    denormalizeState(normalizedState) {
        if (normalizedState.stuff) {
            normalizedState.stuff.forEach(cargo => {
                this.denormalizeCargo(cargo);
            });

            const category = CategoryStore.getSelected();
            if (category && category.get('name') === 'book_separate_car') {
                _isCargoTypesShown = true;
            } else {
                _isCargoTypesShown = false;
            }
        }

        if (normalizedState.total_weight) {
            _totalMeasurements = _totalMeasurements.set('total_weight', parseFloat(normalizedState.total_weight));
        }

        if (normalizedState.total_volume) {
            _totalMeasurements = _totalMeasurements.set('total_volume', parseFloat(normalizedState.total_volume));
        }
    }

    validate() {

    }

    /**
     * @param {function} callback
     */
    addChangeListener(callback) {
        this.on(CHANGE_EVENT, callback);
    }

    /**
     * @param {function} callback
     */
    removeChangeListener(callback) {
        this.removeListener(CHANGE_EVENT, callback);
    }

    emitChange() {
        this.emit(CHANGE_EVENT);
    }

    dispatchToken = Dispatcher.register((action) => {
        const payload = action.payload;

        switch (action.type) {
            case Actions.SELECT_CATEGORY:
                Dispatcher.waitFor([CategoryStore.dispatchToken]);
                initializeCargos();
                break;

            case Actions.ADD_DELIVERY_CARGO:
                addDeliveryCargo(payload.cargoType);
                break;

            case Actions.REMOVE_DELIVERY_CARGO:
                removeDeliveryCargo(payload.cargoId);
                break;

            case Actions.UPDATE_DELIVERY_CARGO:
                updateDeliveryCargo(payload.cargoId, payload.key, payload.value);
                break;

            case Actions.REMOVE_CARGO:
                removeCargo(payload.cargoId);
                break;

            case Actions.ADD_SUBCATEGORY:
                _isCargoTypesShown = true;
                break;

            case Actions.START_CARGO_EDITING:
                _editingCargoIndex = payload.cargoId;
                break;

            case Actions.STOP_CARGO_EDITING:
                stopCargoEditing();
                updateTotalMeasurements();
                _isCargoTypesShown = false;
                _editingCargoIndex = -1;
                break;

            case Actions.CANCEL_CARGO_EDITING:
                _editingCargoIndex = -1;
                break;

            case Actions.GENERATE_CARGO:
                generateCargo(payload.cargo, payload.attributes, payload.clearDelivery);
                break;

            case Actions.CHANGE_TOTAL_MEASUREMENTS:
                changeTotalMeasurements(payload);
                break;

            case Actions.SET_TOTAL_MEASUREMENTS:
                setTotalMeasurements(payload.weight, payload.volume, payload.isAutoTotalMeasurementsCountOn);
                break;

            default:
                return;
        }

        this.emitChange();
    })
}

export default new CargosStore();



// WEBPACK FOOTER //
// ./client/stores/CargosStore.js